// Copyright 2019 Sorama B.V.

import { computedFrom } from "aurelia-binding";
import { FrameGenerator } from "controls/griddataplayer/GridDataPlayer";
import { AbsoluteValueGridData, DataOrder, Grid, GridData } from "controls/plot/grid-plot/GridData";
import { Margin, Size } from "controls/plot/IPlotPart";
import { HolographyProjection, HolographyProjectionMode } from "datatypes/SoramaWebApi";
import { IDataExporter, Set } from "libraries/Export/IDataExporter";
import { IExportable } from "libraries/Export/IExportable";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { HorizontalAnchor, RelativePosition, VerticalAnchor } from "libraries/SVG/ISVGComposer";
import { SVGComposer } from "libraries/SVG/SVGComposer";
import { SVGNode } from "libraries/SVG/SVGNode";
import { AcousticIntensity } from "units/AcousticIntensity";
import { Generic } from "units/Generic";
import { Length } from "units/Length";
import { Phase } from "units/Phase";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";
import { Velocity } from "units/Velocity";

export class NamedProjectionMode {
    public static get pressure(): NamedProjectionMode {
        return { name: "Pressure", value: HolographyProjectionMode.Pressure };
    }
    public static get velocity(): NamedProjectionMode {
        return { name: "Velocity", value: HolographyProjectionMode.Velocity };
    }
    public static get intensity(): NamedProjectionMode {
        return { name: "Intensity", value: HolographyProjectionMode.Intensity };
    }

    public static get all(): NamedProjectionMode[] {
        return [NamedProjectionMode.pressure, NamedProjectionMode.velocity, NamedProjectionMode.intensity];
    }

    public static fromProjectionMode(projectionMode: HolographyProjectionMode) {
        return NamedProjectionMode.all.find((x) => x.value === projectionMode);
    }

    public name: string;
    public value: HolographyProjectionMode;
}

interface NearfieldProjectionSetContext {
    projections: NearfieldProjectionContext[];
}

interface NearfieldProjectionContext {
    data: GridData;
    minValue: UnitValue;
    maxValue: UnitValue;

    projectionMode: NamedProjectionMode;
    projection: HolographyProjection;

    fixedMinimum?: UnitValue;
    fixedMaximum?: UnitValue;

    absMin?: number;
    absMax?: number;
    absValues?: number[];

    dataExportable?: IExportable;
    colorbarExportable?: IExportable;
}

/**
 * View model for a set of projections shown in a nearfield module
 */
export class NearfieldProjectionSetModel implements FrameGenerator<NearfieldProjectionSetContext>, IExportable {
    private readonly projectionSetContext: NearfieldProjectionSetContext;
    private readonly grid: Grid;

    public readonly keyUnit: Unit = Phase.degree;
    public readonly minKey: UnitValue = new UnitValue(0, this.keyUnit);
    public readonly maxKey: UnitValue = new UnitValue(360, this.keyUnit);

    public readonly projections: HolographyProjection[];
    public readonly colorBarHeight: number = 30;

    public idealAspectRatio = 1;

    @computedFrom("idealAspectRatio")
    public get gridLayout(): Size {
        if (this.projections.length === 0) return { width: 1, height: 1 };

        let minError = Number.POSITIVE_INFINITY;
        let bestLayout = { width: this.projections.length, height: 1 };

        // Try to optimally fill up our space
        for (let w = 1; w <= this.projections.length; w++) {
            const h = Math.ceil(this.projections.length / w);
            const aspect = (this.grid.dimension.x * w) / (this.grid.dimension.y * h);

            const error = Math.abs(this.idealAspectRatio - aspect);
            if (error < minError && aspect >= this.idealAspectRatio) {
                minError = error;
                bestLayout = { width: w, height: h };
            }
        }

        return bestLayout;
    }

    @computedFrom("gridLayout")
    public get gridTemplate(): string {
        // Compute grid layout
        const gridLayout = this.gridLayout;
        let columns = "grid-template-columns:";
        const colWidth = (100 / gridLayout.width).toFixed(3) + "%";

        for (let i = 0; i < gridLayout.width; i++) {
            columns += " " + colWidth;
        }

        let rows = "grid-template-rows:";
        const rowHeight = (100 / gridLayout.height).toFixed(3) + "%";

        for (let i = 0; i < gridLayout.height; i++) {
            rows += " " + rowHeight;
        }

        return columns + ";" + rows + ";";
    }

    @computedFrom("gridLayout", "grid")
    public get gridAspectSize(): Size {
        if (!this.grid) {
            return { width: 1, height: 1 };
        }

        const layout = this.gridLayout;

        return { width: layout.width * this.grid.dimension.x, height: layout.height * this.grid.dimension.y };
    }

    @computedFrom("gridLayout")
    public get gridMargin(): Margin {
        return {
            left: 0,
            right: this.gridLayout.width * 2,
            top: 0,
            bottom: this.gridLayout.height * (this.colorBarHeight + 2),
        };
    }

    constructor(projections: HolographyProjection[], grid: Grid, idealAspectRadio: number) {
        this.projections = projections;
        this.grid = grid;
        this.idealAspectRatio = idealAspectRadio;

        this.projectionSetContext = {
            projections: this.projections.map((x) => ({
                minValue: new UnitValue(0, Generic.unitless),
                maxValue: new UnitValue(0, Generic.unitless),
                data: undefined,
                projectionMode: NamedProjectionMode.fromProjectionMode(x.properties.mode),
                projection: x,
            })),
        };

        // Initialize private fields
        this.initializeProjectionSetContext(this.projectionSetContext);

        // Generate first frame
        this.generate(this.minKey, this.projectionSetContext);
    }

    //#region FrameGenerator<ProjectionSetContext>

    public initialize(): NearfieldProjectionSetContext {
        return this.projectionSetContext;
    }

    public generate(key: UnitValue, context: NearfieldProjectionSetContext): void {
        // Generate frame data
        for (const projectionContext of context.projections) {
            switch (projectionContext.projectionMode.value) {
                case HolographyProjectionMode.Intensity:
                    this.generateAbsolute(projectionContext);
                    break;
                case HolographyProjectionMode.Pressure:
                case HolographyProjectionMode.Velocity:
                    this.generateAnimated(key, projectionContext);
                    break;
                default:
                    throw new Error("Invalid Holography Projection Mode");
            }
        }
    }

    //#endregion

    //#region IExportable

    public exportToSvg(suggestedWidth?: number, suggestedHeight?: number): Promise<SVGNode> {
        if (this.projectionSetContext.projections.length === 0) {
            return Promise.reject("No exportable data available");
        }

        const widthPerProjection =
            suggestedWidth && suggestedHeight ? suggestedWidth / this.gridLayout.width : undefined;
        const heightPerProjection =
            suggestedWidth && suggestedHeight ? suggestedHeight / this.gridLayout.height : undefined;

        // Combine projections into a single svg
        const svgPromises = this.projectionSetContext.projections.map((projection) => {
            if (!projection.dataExportable || !projection.colorbarExportable)
                return Promise.reject("No exportable data available");

            return Promise.all([
                projection.dataExportable.exportToSvg(widthPerProjection, heightPerProjection),
                projection.colorbarExportable.exportToSvg(),
            ]).then((nodes) => {
                // Combine
                const composer = new SVGComposer(nodes[0]);
                composer.append(nodes[1], RelativePosition.Bottom);
                return composer.generate();
            });
        });

        return Promise.all(svgPromises).then((svgs) => {
            // Combine all columns
            let index = 0;
            const rowSvgs: SVGNode[] = new Array(this.gridLayout.height);
            for (let i = 0; i < this.gridLayout.height; i++) {
                let svgNode = svgs[index++];
                if (this.gridLayout.width > 1) {
                    const rowComposer = new SVGComposer(svgNode);
                    for (let j = 1; j < this.gridLayout.width && index < svgs.length; j++) {
                        rowComposer.append(svgs[index++], RelativePosition.Right);
                    }

                    svgNode = rowComposer.generate();
                }

                rowSvgs[i] = svgNode;
            }

            // Combine all rows
            if (this.gridLayout.height === 1) {
                return rowSvgs[0];
            } else {
                const composer = new SVGComposer(rowSvgs[0]);
                composer.anchor(HorizontalAnchor.Left, VerticalAnchor.Middle);
                for (let i = 1; i < rowSvgs.length; i++) {
                    composer.append(rowSvgs[i], RelativePosition.Bottom);
                }

                return composer.generate();
            }
        });
    }

    public export(exporter: IDataExporter, fileName: string) {
        // Generate sets
        const sets: Set[] = new Array(3);
        sets[0] = { label: "x", data: new Array(this.grid.dimension.x * this.grid.dimension.y), yUnit: Length.meter };
        sets[1] = { label: "y", data: new Array(this.grid.dimension.x * this.grid.dimension.y), yUnit: Length.meter };
        sets[2] = { label: "z", data: new Array(this.grid.dimension.x * this.grid.dimension.y), yUnit: Length.meter };

        const zValue = this.projectionSetContext.projections[0].projection.properties.distance;
        let i = 0;

        for (let y = 0; y < this.grid.dimension.y; y++) {
            const yValue = new UnitValue(
                this.grid.spacing.y * (y - this.grid.dimension.y * 0.5 + 0.5) + this.grid.center.y,
                this.grid.unit
            ).toUnit(Length.meter);
            for (let x = 0; x < this.grid.dimension.x; x++, i++) {
                const xValue = new UnitValue(
                    this.grid.spacing.x * (x - this.grid.dimension.x * 0.5 + 0.5) + this.grid.center.x,
                    this.grid.unit
                ).toUnit(Length.meter);
                sets[0].data[i] = xValue;
                sets[1].data[i] = yValue;
                sets[2].data[i] = zValue;
            }
        }

        for (let j = 0; j < this.projectionSetContext.projections.length; j++) {
            const projectionContext = this.projectionSetContext.projections[j];

            const realSet: Set = {
                label: projectionContext.projectionMode.name + " (Real Part)",
                data: new Array(this.grid.dimension.x * this.grid.dimension.y),
                yUnit: projectionContext.data.unit,
            };
            const imagSet: Set = {
                label: projectionContext.projectionMode.name + " (Imaginary Part)",
                data: new Array(this.grid.dimension.x * this.grid.dimension.y),
                yUnit: projectionContext.data.unit,
            };

            for (let i = 0; i < projectionContext.projection.data.length; i++) {
                const p = projectionContext.projection.data[i];
                realSet.data[i] = p.re.toExponential(8);
                imagSet.data[i] = p.im.toExponential(8);
            }

            sets.push(realSet, imagSet);
        }

        const csv = exporter.exportSets(sets);
        FileDownloader.downloadAsFile(csv, "text/plain", fileName + ".csv");
    }

    //#endregion

    private initializeProjectionSetContext(context: NearfieldProjectionSetContext): void {
        for (const projectionContext of context.projections) {
            const projection = projectionContext.projection;

            // Use the abs values
            if (!projectionContext.absValues) {
                let max = Number.NEGATIVE_INFINITY;
                let min = Number.POSITIVE_INFINITY;

                projectionContext.absValues = new Array(projection.data.length);
                for (let j = 0; j < projection.data.length; j++) {
                    const hPoint = projection.data[j];
                    projectionContext.absValues[j] = Math.sqrt(hPoint.re * hPoint.re + hPoint.im * hPoint.im);

                    max = Math.max(max, projectionContext.absValues[j]);
                    min = Math.min(min, projectionContext.absValues[j]);
                }

                projectionContext.absMin = min;
                projectionContext.absMax = max;
            }
        }
    }

    private generateAbsolute(projectionContext: NearfieldProjectionContext): void {
        const projection = projectionContext.projection;
        const unit = NearfieldProjectionSetModel.toProjectionUnit(projection.properties.mode);

        projectionContext.minValue = new UnitValue(projectionContext.absMin, unit);
        projectionContext.maxValue = new UnitValue(projectionContext.absMax, unit);

        projectionContext.data = new GridData(
            projectionContext.absValues,
            unit,
            DataOrder.LeftToRightThenTopToBottom,
            this.grid
        );
    }

    private generateAnimated(key: UnitValue, projectionContext: NearfieldProjectionContext): void {
        const phase = key.toUnit(Phase.radian);
        const pReal = Math.cos(phase);
        const pImag = Math.sin(phase);

        const projection = projectionContext.projection;

        const hologramData =
            projectionContext.data && projectionContext.data.data.length === projection.data.length
                ? projectionContext.data.data
                : <number[]>new Array(projection.data.length);

        for (let j = 0; j < projection.data.length; j++) {
            const hPoint = projection.data[j];
            hologramData[j] = hPoint.re * pReal - hPoint.im * pImag;
        }

        const unit = NearfieldProjectionSetModel.toProjectionUnit(projection.properties.mode);
        projectionContext.minValue = new UnitValue(-projectionContext.absMax, unit);
        projectionContext.maxValue = new UnitValue(projectionContext.absMax, unit);

        projectionContext.data = new AbsoluteValueGridData(
            hologramData,
            projectionContext.absValues,
            unit,
            DataOrder.LeftToRightThenTopToBottom,
            this.grid
        );
    }

    private static toProjectionUnit(projectionMode: HolographyProjectionMode): Unit {
        switch (projectionMode) {
            case HolographyProjectionMode.Intensity:
                return AcousticIntensity.Intensity;
            case HolographyProjectionMode.Velocity:
                return Velocity.meterPerSecond;
            case HolographyProjectionMode.Pressure:
                return Pressure.pascal;
            default:
                throw new Error("Invalid Holography Projection Mode");
        }
    }
}
